import { API_CONFIG, BOOKING_TYPES } from '../utils/constants';

class APIService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.tenantCode = API_CONFIG.TENANT_CODE;
    if (!this.baseURL) {
      console.error('Missing REACT_APP_API_URL environment variable. Set REACT_APP_API_URL in .env or provide it at runtime.');
      throw new Error('REACT_APP_API_URL is not defined. Aborting APIService initialization.');
    }
  }

  /**
   * 🆕 Helper to extract error message from response data
   */
  async _getErrorMessage(response) {
    try {
      const data = await response.json();
      // Handle different error structures from backend
      return data.message || data.error?.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
    } catch (e) {
      return `HTTP ${response.status}: ${response.statusText}`;
    }
  }

  async request(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();
      if (!response.ok && !data.ok) {
        throw new Error(data.message || data.error?.message || 'API request failed');
      }
      return data.data || data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // 🆕 NEW: Separate method for authentication requests
  async authRequest(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': this.tenantCode,  // Use lowercase for auth endpoints
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Return data directly - auth endpoints don't use ok/data wrapper
    } catch (error) {
      console.error('Auth API Error:', error);
      throw error;
    }
  }

  // Currency endpoint
  async getTenantCurrencies() {
    try {
      const response = await fetch(`${this.baseURL}/currencies/tenant`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        }
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
      throw error;
    }
  }

  async searchTransferVehicles(params) {
    const searchParams = {
      bookingType: params.type || BOOKING_TYPES.AIRPORT_TRANSFER,
      pax: params.passengers || 1,
      bags: params.luggage || 0,
      pickupAt: params.pickupAt || new Date().toISOString(),
      currency: params.currency || 'USD'
    };

    try {
      // Updated endpoint
      const response = await fetch(`${this.baseURL}/search/vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        },
        body: JSON.stringify(searchParams)
      });

      const data = await response.json();

      // Check if it's hourly or transfer type
      const isHourlyType = params.type === BOOKING_TYPES.AIRPORT_HOURLY ||
        params.type === BOOKING_TYPES.CITY_HOURLY;

      // Process vehicles based on response structure
      if (data.vehicles) {
        data.vehicles = data.vehicles.map(vehicle => {
          let pricing = {};

          if (isHourlyType && vehicle.hourlyPricing) {
            // Hourly pricing structure
            // Calculate hourly total immediately
            const hours = params.hours || 2;
            const hourlyRate = parseFloat(vehicle.hourlyPricing.hourlyRate);
            const totalAmount = hourlyRate * hours;

            pricing = {
              ...vehicle.hourlyPricing,
              hourlyRate: hourlyRate,
              totalAmount: totalAmount,  // Add calculated total
              overagePerKm: parseFloat(vehicle.hourlyPricing.overagePerKm || 0),
              waitingChargePerMin: parseFloat(vehicle.hourlyPricing.waitingChargePerMin || 0),
              isHourly: true,
              hours: hours  // Store hours in pricing
            };
          } else if (vehicle.transferPricing) {
            // Transfer pricing structure
            pricing = {
              ...vehicle.transferPricing,
              baseFare: parseFloat(vehicle.transferPricing.baseFare),
              pricePerKm: parseFloat(vehicle.transferPricing.pricePerKm),
              pricePerMin: parseFloat(vehicle.transferPricing.pricePerMin),
              waitingChargePerMin: parseFloat(vehicle.transferPricing.waitingChargePerMin),
              isHourly: false
            };
          }

          return {
            ...vehicle,
            vehicleCode: vehicle.vehicleCode,
            className: vehicle.vehicleName,
            classId: vehicle.vehicleId,
            paxMax: vehicle.maxPassengers || 4,
            bagMax: vehicle.maxLuggage || 2,
            pricing: pricing
          };
        });
      }

      // Add hours to the response if it's hourly
      if (isHourlyType && params.hours) {
        data.hours = params.hours;
      }

      return data;
    } catch (error) {
      console.error('Vehicle search failed:', error);
      throw error;
    }
  }

  async getVehicleExtras(currency) {
    try {
      // Don't use this.request() - handle the response directly
      const url = currency 
        ? `${this.baseURL}/search/vehicle-extras?currency=${encodeURIComponent(currency)}`
        : `${this.baseURL}/search/vehicle-extras`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data; // Return the array directly

    } catch (error) {
      console.error('Failed to fetch vehicle extras:', error);
      // Return fallback data if API fails
      return [
        { code: "child_seat", name: "Child Seat", price: 5, currency: "EUR", maxQuantity: 2, description: "Safety seat for children" },
        { code: "booster_seat", name: "Booster Seat", price: 3, currency: "EUR", maxQuantity: 2, description: "Booster seat for older children" },
        { code: "wifi", name: "WiFi Hotspot", price: 10, currency: "EUR", maxQuantity: 1, description: "Mobile WiFi hotspot for your journey" },
        { code: "meet_greet", name: "Meet & Greet Service", price: 15, currency: "EUR", maxQuantity: 1, description: "Driver will meet you at arrivals with name board" }
      ];
    }
  }

  // Updated searchTransfer to handle all transfer types
  async searchTransfer(params) {
    try {
      // Minimal payload required by /api/search/vehicles for TRANSFER searches
      const body = {
        bookingType: params.type,            // e.g. "airport_transfer" / "city_point_to_point"
        pax: params.pax ?? params.passengers ?? 1,
        bags: params.bags ?? params.luggage ?? 0,
        pickupAt: params.pickupAt,           // ISO string
        currency: params.currency || 'USD',
      };

      const res = await fetch(`${this.baseURL}/search/vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // NOTE: use the header from the guide
          'x-tenant': this.tenantCode,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        console.error('Search /search/vehicles failed:', res.status, data);
        throw new Error(data?.error?.message || 'Transfer search failed');
      }

      // Backend returns: { searchId, bookingType, vehicles: [...], ... }
      // Sort vehicles by displayOrder if available
      const sortedVehicles = (data.vehicles || []).sort((a, b) =>
        (a.displayOrder ?? a.display_order ?? 99) - (b.displayOrder ?? b.display_order ?? 99)
      );

      // Normalize to what the UI expects: { options: [...] }
      const options = sortedVehicles.map(v => {
        const tp = v.transferPricing || {};

        return {
          ...v,
          classId: v.vehicleId || v.vehicleCode || v.id,
          className: v.vehicleName || v.vehicleCode || 'Vehicle',
          description: v.description || '',
          paxMax: v.maxPassengers ?? 4,
          bagMax: v.maxLuggage ?? 2,
          // Initial price: use baseFare so UI shows a number immediately.
          // Later, you can recompute using route distance/time.
          pricing: {
            totalAmount: typeof tp.baseFare === 'number' ? tp.baseFare : 0,
            baseAmount: typeof tp.baseFare === 'number' ? tp.baseFare : 0,
            currency: tp.currency || data.currency || body.currency || 'USD',
            breakdown: {
              baseFare: tp.baseFare ?? 0,
              // distanceRateKm is the new field, pricePerKm is the fallback/alias
              pricePerKm: tp.distanceRateKm ?? tp.pricePerKm ?? 0,
              pricePerMin: tp.pricePerMin ?? 0,
              freeWaitingMins: tp.freeWaitingTimeMin ?? tp.freeWaitingMins ?? 0,
              waitingChargePerMin: tp.waitingTimeRateMin ?? tp.waitingChargePerMin ?? 0,
              peakMultipliers: tp.peakMultipliers || []
            },
            surcharges: [],
            discounts: [],
            isHourly: false,
          },
          // Keep raw for future recalculation with distance/duration
          meta: { raw: v },
        };
      });

      return {
        searchId: data.searchId || `search_${Date.now()}`,
        type: data.bookingType || params.type,
        options,
        validUntil: data.validUntil,
        pricingNotes: data.pricingNotes || [],
        // (optional) include raw if you need it later
        _raw: data,
      };
    } catch (error) {
      console.error('Vehicle search failed:', error);
      throw error;
    }
  }

  // Updated searchHourly to handle both airport and city hourly services
  async searchHourly(params) {
    try {
      // Expect params: { type, hours, passengers/pax, luggage/bags, pickupAt, currency }
      const body = {
        bookingType: params.type,                  // e.g. "airport_hourly" | "city_hourly"
        pax: params.pax ?? params.passengers ?? 1,
        bags: params.bags ?? params.luggage ?? 0,
        pickupAt: params.pickupAt,                 // ISO
        currency: params.currency || 'USD',
      };

      const res = await fetch(`${this.baseURL}/search/vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Code': this.tenantCode,        // use your tenant header
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        console.error('Search /search/vehicles (hourly) failed:', res.status, data);
        throw new Error(data?.error?.message || 'Hourly search failed');
      }

      const hours = Number(params.hours) || 1;

      // Sort vehicles by displayOrder if available
      const sortedVehicles = (data.vehicles || []).sort((a, b) =>
        (a.displayOrder ?? a.display_order ?? 99) - (b.displayOrder ?? b.display_order ?? 99)
      );

      // vehicles[] -> options[] for UI
      const options = sortedVehicles.map(v => {
        const hp = v.hourlyPricing || {};
        const hourlyRate = Number(hp.hourlyRate ?? 0);

        // Simple total: hours * hourlyRate (you can add overage/waiting in UI)
        const total = hourlyRate * hours;

        return {
          classId: v.vehicleId || v.vehicleCode || v.id,
          className: v.vehicleName || v.vehicleCode || 'Vehicle',
          description: v.description || '',
          paxMax: v.maxPassengers ?? 4,
          bagMax: v.maxLuggage ?? 2,
          pricing: {
            totalAmount: Number.isFinite(total) ? total : 0,
            baseAmount: Number.isFinite(total) ? total : 0,
            currency: hp.currency || data.currency || body.currency || 'USD',
            breakdown: {
              // baseHourRate is the new field, hourlyRate is the fallback/alias
              hourlyRate: hp.baseHourRate ?? hp.hourlyRate ?? 0,
              hours: hours,
              includedKmPerHour: hp.includedKmPerHour ?? 0,
              overagePerKm: hp.overagePerKm ?? 0,
              freeWaitingMins: hp.freeWaitingTimeMin ?? hp.freeWaitingMins ?? 0,
              waitingChargePerMin: hp.waitingTimeRateMin ?? hp.waitingChargePerMin ?? 0,
              minHours: hp.minHours ?? 0,
              maxHours: hp.maxHours ?? 0,
              peakMultipliers: hp.peakMultipliers || []
            },
            surcharges: [],   // add in UI if you have any
            discounts: [],    // add in UI if you have any
            isHourly: true,
            hours,
          },
          meta: { raw: v },
        };
      });

      return {
        searchId: data.searchId || `search_${Date.now()}`,
        type: data.bookingType || params.type,
        options,
        validUntil: data.validUntil,
        pricingNotes: data.pricingNotes || [],
        _raw: data,
      };
    } catch (error) {
      console.error('Hourly search failed:', error);
      throw error;
    }
  }

  async createBooking(bookingData, token = null) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant': this.tenantCode,
      };

      // ✅ Add Authorization header if token provided
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const responseData = await response.json();
      return responseData;

    } catch (error) {
      console.error('Failed to create booking:', error);
      throw error;
    }
  }

  // 🆕 UPDATED: Enhanced payment intent creation with proper providerRef handling
  async createPaymentIntent(paymentData) {
    try {
      const response = await fetch(`${this.baseURL}/payments/intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const responseData = await response.json();

      // 🆕 CRITICAL: Store providerRef for WebXPay confirmation
      if (responseData.provider === 'webxpay' && responseData.providerRef) {
        console.log('🔍 DEBUG: Storing WebXPay providerRef:', responseData.providerRef);
        sessionStorage.setItem('webxpay_provider_ref', responseData.providerRef);
        sessionStorage.setItem('webxpay_payment_id', responseData.id);
      }

      return responseData;

    } catch (error) {
      console.error('Failed to create payment intent:', error);
      throw error;
    }
  }

  // 🆕 UPDATED: Payment confirmation using correct endpoint pattern
  async confirmPayment(paymentIntentId) {
    try {
      const response = await fetch(`${this.baseURL}/payments/${paymentIntentId}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        }
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const responseData = await response.json();
      return responseData;

    } catch (error) {
      console.error('Failed to confirm payment:', error);
      throw error;
    }
  }

  // ===== EXISTING WEBXPAY REDIRECT METHODS (KEEP FOR BACKWARD COMPATIBILITY) =====

  // 🆕 NEW: WebXPay specific confirmation using providerRef
  async confirmWebXPayPayment(transactionData = {}) {
    try {
      // 🆕 CRITICAL: Get stored providerRef (booking code)
      const providerRef = sessionStorage.getItem('webxpay_provider_ref');

      if (!providerRef) {
        throw new Error('No WebXPay booking code found. Payment session may have expired.');
      }

      console.log('🔍 DEBUG: Confirming WebXPay payment with providerRef:', providerRef);

      // 🆕 UPDATED: Use correct confirmation endpoint pattern
      const response = await fetch(`${this.baseURL}/payments/webxpay/${providerRef}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        },
        body: JSON.stringify({
          status: transactionData.status || 'success',
          transactionId: transactionData.transactionId || `TXN_${Date.now()}`,
          amount: transactionData.amount,
          ...transactionData
        })
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const responseData = await response.json();

      // 🆕 Clean up stored data after successful confirmation
      sessionStorage.removeItem('webxpay_provider_ref');
      sessionStorage.removeItem('webxpay_payment_id');

      return responseData;

    } catch (error) {
      console.error('Failed to confirm WebXPay payment:', error);
      throw error;
    }
  }

  // 🆕 UPDATED: WebXPay redirect method
  async redirectToWebXPay(formData) {
    try {
      // Create a hidden form and submit it to redirect to WebXPay
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = formData.redirectUrl;
      form.style.display = 'none';

      // Add all form fields from the backend response
      Object.entries(formData.formData || {}).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      console.error('Failed to redirect to WebXPay:', error);
      throw error;
    }
  }

  // 🆕 UPDATED: Check payment status using stored providerRef
  async getWebXPayPaymentStatus() {
    try {
      const paymentId = sessionStorage.getItem('webxpay_payment_id');
      const providerRef = sessionStorage.getItem('webxpay_provider_ref');

      if (!paymentId) {
        throw new Error('No WebXPay payment ID found');
      }

      console.log('🔍 DEBUG: Checking payment status for:', { paymentId, providerRef });

      const response = await fetch(`${this.baseURL}/payments/${paymentId}/status`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': this.tenantCode,
        }
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const responseData = await response.json();
      return responseData;

    } catch (error) {
      console.error('Failed to get payment status:', error);
      throw error;
    }
  }

  // 🆕 NEW: Helper method to get stored WebXPay data
  getStoredWebXPayData() {
    return {
      providerRef: sessionStorage.getItem('webxpay_provider_ref'),
      paymentId: sessionStorage.getItem('webxpay_payment_id')
    };
  }

  // 🆕 NEW: Helper method to clear WebXPay session data
  clearWebXPaySession() {
    sessionStorage.removeItem('webxpay_provider_ref');
    sessionStorage.removeItem('webxpay_payment_id');
  }

  // ===== NEW WEBXPAY EMBEDDED/TOKENIZE METHODS =====

  // 🆕 NEW: Initialize WebXPay embedded session
  async initWebXPaySession(bookingId, currency = 'LKR', cardType = 'visa_master') {
    try {
      const response = await fetch(`${this.baseURL}/payments/webxpay/tokenize/init-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': this.tenantCode,
        },
        body: JSON.stringify({
          bookingId,
          currency,
          cardType
        })
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Returns: { hostedSessionUrl, merchantId, currency, bookingCode, amount }

    } catch (error) {
      console.error('Failed to initialize WebXPay session:', error);
      throw error;
    }
  }

  // 🆕 NEW: Process one-time payment with session
  async processWebXPaySession(sessionId, bookingId, currency, amount = null) {
    try {
      const response = await fetch(`${this.baseURL}/payments/webxpay/tokenize/pay-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': this.tenantCode,
        },
        body: JSON.stringify({
          sessionId,
          bookingId,
          currency,
          ...(amount && { amount })
        })
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Returns: { success, requires3ds, html3ds, bookingCode, error }

    } catch (error) {
      console.error('Failed to process WebXPay session:', error);
      throw error;
    }
  }

  // 🆕 NEW: Save card and process payment (for authenticated users)
  async saveCardAndPay(sessionId, bookingId, currency, amount = null, token) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-tenant': this.tenantCode,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/payments/webxpay/tokenize/save-card`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId,
          bookingId,
          currency,
          ...(amount && { amount })
        })
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Returns: { success, requires3ds, html3ds, bookingCode, cardId, error }

    } catch (error) {
      console.error('Failed to save card and pay:', error);
      throw error;
    }
  }

  // 🆕 NEW: Get saved cards (for future implementation)
  async getSavedCards(token) {
    try {
      const response = await fetch(`${this.baseURL}/payments/webxpay/tokenize/cards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant': this.tenantCode,
        }
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Returns array of saved cards

    } catch (error) {
      console.error('Failed to get saved cards:', error);
      throw error;
    }
  }

  // 🆕 NEW: Pay with saved card (for future implementation)
  async payWithSavedCard(savedCardId, bookingId, currency, amount = null, token) {
    try {
      const response = await fetch(`${this.baseURL}/payments/webxpay/tokenize/pay-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant': this.tenantCode,
        },
        body: JSON.stringify({
          savedCardId,
          bookingId,
          currency,
          ...(amount && { amount })
        })
      });

      if (!response.ok) {
        const message = await this._getErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      return data; // Returns: { success, requires3ds, html3ds, bookingCode, error }

    } catch (error) {
      console.error('Failed to pay with saved card:', error);
      throw error;
    }
  }
}

export default new APIService();